import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { ResolvedCampaignContent } from '../config/types';
import { brand } from '@/app/theme/tokens';

const BENEFIT_ICONS = [
  ExploreOutlinedIcon,
  GroupsOutlinedIcon,
  PersonOutlineIcon,
  EventAvailableOutlinedIcon,
] as const;

interface GuideRecruitmentPopupProps {
  open: boolean;
  content: ResolvedCampaignContent | null;
  onPrimaryCta: () => void;
  onDismiss: (method: 'maybe_later' | 'close') => void;
}

const MotionBox = motion.create(Box);

export function GuideRecruitmentPopup({
  open,
  content,
  onPrimaryCta,
  onDismiss,
}: GuideRecruitmentPopupProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useReducedMotion();

  if (!content || typeof document === 'undefined') return null;

  const reduced = Boolean(prefersReducedMotion);
  const ctaLabel = content.cta.replace(/\s*→\s*$/, '').trim();

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal + 2,
        pointerEvents: 'none',
      }}
    >
      <AnimatePresence>
        {open ? (
          <MotionBox
            key="guide-campaign-backdrop"
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduced ? undefined : { opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => onDismiss('close')}
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'auto',
              bgcolor: alpha(brand.navy, 0.42),
              backdropFilter: 'blur(2px)',
            }}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {open ? (
          <MotionBox
            key="guide-campaign-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guide-campaign-headline"
            aria-describedby="guide-campaign-description"
            initial={
              reduced
                ? false
                : isMobile
                  ? { y: '110%', opacity: 0.98 }
                  : { x: '-50%', y: 'calc(-50% + 28px)', opacity: 0, scale: 0.96 }
            }
            animate={
              isMobile
                ? { y: 0, opacity: 1, scale: 1 }
                : { x: '-50%', y: '-50%', opacity: 1, scale: 1 }
            }
            exit={
              reduced
                ? undefined
                : isMobile
                  ? { y: '110%', opacity: 0.98 }
                  : { x: '-50%', y: 'calc(-50% + 16px)', opacity: 0, scale: 0.97 }
            }
            transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.85 }}
            sx={{
              position: 'fixed',
              pointerEvents: 'auto',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: brand.white,
              color: brand.textPrimary,
              borderRadius: { xs: '24px 24px 0 0', sm: 3.5 },
              border: `1px solid ${alpha(brand.navy, 0.08)}`,
              boxShadow: `0 28px 64px ${alpha(brand.navy, 0.22)}`,
              ...(isMobile
                ? {
                    left: 0,
                    right: 0,
                    bottom: 0,
                    maxHeight: 'min(86vh, 720px)',
                  }
                : {
                    top: '50%',
                    left: '50%',
                    width: 'min(420px, calc(100vw - 48px))',
                    maxHeight: 'min(90vh, 720px)',
                  }),
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: -80,
                right: -60,
                width: 220,
                height: 220,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(brand.blue400, 0.28)} 0%, transparent 70%)`,
                pointerEvents: 'none',
                zIndex: 0,
              }}
            />

            <IconButton
              aria-label="Close guide recruitment message"
              onClick={() => onDismiss('close')}
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 3,
                color: brand.white,
                bgcolor: alpha(brand.navy, 0.38),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(brand.white, 0.18)}`,
                '&:hover': {
                  bgcolor: alpha(brand.navy, 0.55),
                  color: brand.white,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Box
              sx={{
                position: 'relative',
                height: { xs: 176, sm: 196 },
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Box
                component={motion.img}
                src={content.image}
                alt={content.imageAlt}
                loading="eager"
                initial={reduced ? false : { scale: 1.12 }}
                animate={reduced ? undefined : { scale: 1 }}
                transition={{ duration: 8, ease: [0.22, 1, 0.36, 1] }}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center 32%',
                  display: 'block',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: `
                    linear-gradient(180deg, ${alpha(brand.navy, 0.15)} 0%, ${alpha(brand.navy, 0.05)} 35%, ${alpha(brand.navy, 0.55)} 78%, ${brand.white} 100%),
                    linear-gradient(90deg, ${alpha(brand.blue700, 0.25)} 0%, transparent 55%)
                  `,
                }}
              />

              {content.overline ? (
                <MotionBox
                  initial={reduced ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18, duration: 0.35 }}
                  sx={{
                    position: 'absolute',
                    left: 20,
                    bottom: 28,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: alpha(brand.white, 0.16),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(brand.white, 0.28)}`,
                  }}
                >
                  <Box
                    component={motion.span}
                    animate={
                      reduced
                        ? undefined
                        : {
                            scale: [1, 1.25, 1],
                            opacity: [0.7, 1, 0.7],
                          }
                    }
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: brand.blue300,
                      boxShadow: `0 0 0 3px ${alpha(brand.blue300, 0.28)}`,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      color: brand.white,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {content.overline}
                  </Typography>
                </MotionBox>
              ) : null}
            </Box>

            <MotionBox
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: reduced ? 0 : 0.07,
                    delayChildren: reduced ? 0 : 0.12,
                  },
                },
              }}
              sx={{
                position: 'relative',
                zIndex: 1,
                px: { xs: 2.5, sm: 3 },
                pt: 0.5,
                pb: { xs: 2.75, sm: 3 },
                maxHeight: { xs: 'calc(86vh - 176px)', sm: 'none' },
                overflowY: 'auto',
                background: `
                  radial-gradient(ellipse 90% 60% at 0% 0%, ${alpha(brand.blue50, 0.95)} 0%, transparent 55%),
                  ${brand.white}
                `,
              }}
            >
              <MotionBox variants={itemVariants(reduced)} sx={{ mb: 1.25 }}>
                <Typography
                  id="guide-campaign-headline"
                  component="h2"
                  sx={{
                    fontFamily: '"Playfair Display", Georgia, serif',
                    fontSize: { xs: '1.45rem', sm: '1.6rem' },
                    fontWeight: 700,
                    lineHeight: 1.18,
                    letterSpacing: '-0.025em',
                    color: brand.navy,
                    pr: 1,
                  }}
                >
                  {content.headline}
                </Typography>
              </MotionBox>

              <MotionBox variants={itemVariants(reduced)}>
                <Typography
                  id="guide-campaign-description"
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '0.9375rem' },
                    color: brand.textSecondary,
                    lineHeight: 1.6,
                    mb: content.variant === 'recruit' ? 2 : 2.25,
                  }}
                >
                  {content.description}
                </Typography>
              </MotionBox>

              {content.variant === 'recruit' ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    mb: 2.5,
                  }}
                >
                  {content.benefits.slice(0, 4).map((benefit, index) => {
                    const Icon = BENEFIT_ICONS[index] ?? ExploreOutlinedIcon;
                    return (
                      <MotionBox
                        key={benefit.title}
                        variants={itemVariants(reduced)}
                        whileHover={
                          reduced
                            ? undefined
                            : {
                                y: -2,
                                transition: { type: 'spring', stiffness: 420, damping: 24 },
                              }
                        }
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: `1px solid ${alpha(brand.blue600, 0.12)}`,
                          bgcolor: alpha(brand.blue50, 0.65),
                          cursor: 'default',
                          transition: 'border-color 0.2s ease, background-color 0.2s ease',
                          '&:hover': {
                            borderColor: alpha(brand.blue600, 0.28),
                            bgcolor: brand.blue50,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.25,
                            mb: 0.85,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(brand.blue600, 0.12),
                            color: brand.blue700,
                          }}
                        >
                          <Icon sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            lineHeight: 1.25,
                            color: brand.navy,
                            mb: 0.25,
                          }}
                        >
                          {benefit.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            color: brand.textSecondary,
                            lineHeight: 1.35,
                          }}
                        >
                          {benefit.description}
                        </Typography>
                      </MotionBox>
                    );
                  })}
                </Box>
              ) : null}

              <MotionBox variants={itemVariants(reduced)}>
                <Button
                  fullWidth
                  variant="contained"
                  endIcon={
                    <Box
                      component={motion.span}
                      animate={reduced ? undefined : { x: [0, 4, 0] }}
                      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                      sx={{ display: 'inline-flex' }}
                    >
                      <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                  }
                  onClick={onPrimaryCta}
                  sx={{
                    mb: 1,
                    py: 1.35,
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: brand.blue600,
                    color: brand.white,
                    boxShadow: `0 10px 28px ${alpha(brand.blue600, 0.35)}`,
                    '&:hover': {
                      bgcolor: brand.blue700,
                      boxShadow: `0 14px 32px ${alpha(brand.blue600, 0.42)}`,
                    },
                  }}
                >
                  {ctaLabel}
                </Button>
              </MotionBox>

              <MotionBox variants={itemVariants(reduced)}>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => onDismiss('maybe_later')}
                  sx={{
                    color: brand.textMuted,
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 0.85,
                    borderRadius: 2,
                    '&:hover': {
                      color: brand.textSecondary,
                      bgcolor: alpha(brand.blue600, 0.05),
                    },
                  }}
                >
                  {content.secondaryCta}
                </Button>
              </MotionBox>
            </MotionBox>
          </MotionBox>
        ) : null}
      </AnimatePresence>
    </Box>,
    document.body,
  );
}

function itemVariants(reduced: boolean) {
  if (reduced) {
    return {
      hidden: { opacity: 1, y: 0 },
      visible: { opacity: 1, y: 0 },
    };
  }
  return {
    hidden: { opacity: 0, y: 14 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring' as const, stiffness: 420, damping: 28 },
    },
  };
}
